import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import Loader3D from '../../../components/Loader/Loader3D';
import apiRequest from '../../../api/request';
import DeveloperNote from './DeveloperNote';

/**
 * WHAT IS BEHIND ONE REVENUE NUMBER.
 *
 * Clicking the Revenue cell on the OGD Point Tracker opens this: every payment
 * that counted towards it, one row each, adding up to the exact figure on the cell.
 *
 * IT SHOWS WHO KEYED EACH PAYMENT IN
 *
 * That is the one rule nobody can check by looking at a total. A payment the OGA
 * keyed themselves and a payment Finance keyed for them both count, and the row
 * says which it was. A payment Client Relations keyed never appears, because
 * chasing a renewal on an enrolled student is their work, not the OGA's sale.
 *
 * A student who paid three instalments is three rows. That is deliberate: Revenue
 * is money received, not students, so the rows have to be payments.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "2026-04-07" shown as "07 Apr 2026". */
const prettyDate = (raw) => {
  if (!raw) return '';
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(raw);

  const [, year, month, day] = match;
  return `${day} ${MONTHS[Number(month) - 1]} ${year}`;
};

const inr = (value) =>
  '₹ ' + Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export default function RevenueDetailDialog({
  open,
  onClose,
  person, // { userId, oga, team, revenue }
  fromDate,
  toDate,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !person?.userId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const res = await apiRequest({
        method: 'get',
        url:
          `ogd-point-tracker/revenue?userId=${person.userId}` +
          `&fromDate=${fromDate}&toDate=${toDate}`,
      });
      if (cancelled) return;

      if (res?.status === 'success') {
        setRows(res.data?.rows || []);
        setMeta(res.data?.meta || null);
      } else {
        setError(res?.message || 'Could not load the payments');
      }
      setLoading(false);
    })();

    // a fast second click must not let the first answer overwrite the second
    return () => {
      cancelled = true;
    };
  }, [open, person?.userId, fromDate, toDate]);

  const listed = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  // the cell said one figure and the payments add to another. Rounded to the rupee
  // before comparing, because the cell is rounded for display
  const mismatch =
    !loading &&
    !error &&
    person?.revenue != null &&
    Math.round(Number(person.revenue)) !== Math.round(listed);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // the box is nearly the whole window. These tables are wide and are
      // read side by side against the number on the cell, so scrolling a
      // narrow box was the slowest part of checking a figure.
      PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Revenue · {person?.oga}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {inr(listed)} · {rows.length}{' '}
              {rows.length === 1 ? 'payment' : 'payments'}
              {meta?.students != null && ` · ${meta.students} students`}
              {/* holds are counted in Revenue but not in Admissions or Sales, so
                  the difference between this popup and those two is usually
                  right here. Said on the summary line rather than left to be
                  found by reading every row. */}
              {meta?.holds > 0 &&
                ` · ${meta.holds} of them ${meta.holds === 1 ? 'is a hold' : 'are holds'}, ${inr(
                  meta.holdAmount,
                )}`}
              {person?.team ? ` · ${person.team}` : ''}
              {fromDate ? ` · ${fromDate} to ${toDate}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Loader3D />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="info" icon={<Icon icon="mdi:cash-remove" />}>
            No money received in this period.
          </Alert>
        )}

        {mismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The table shows {inr(person.revenue)} for this period but these payments
            add up to {inr(listed)}. The two should always match.
          </Alert>
        )}

        {!loading && !error && meta?.byFinance > 0 && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<Icon icon="mdi:bank-outline" />}>
            {meta.byFinance} of these were keyed in by Finance rather than by the OGA.
            They still count: Finance is only typing in a bank transfer for the OGA's
            own student.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <TableContainer sx={{ maxHeight: 'calc(92vh - 190px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>#</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell sx={{ width: 120 }}>Admission no</TableCell>
                  <TableCell sx={{ width: 110 }} title="course_enrollment.course_enrollment_id">
                    Enrolment id
                  </TableCell>
                  <TableCell
                    sx={{ minWidth: 180 }}
                    title="The course the payment was made against."
                  >
                    Course
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>Fee type</TableCell>
                  <TableCell align="right" sx={{ width: 130 }}>
                    Amount
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>Received on</TableCell>
                  <TableCell
                    sx={{ width: 180 }}
                    title="Who typed the payment into the portal. The OGA, or Finance on their behalf."
                  >
                    Keyed in by
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, index) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{r.name || ''}</TableCell>
                    <TableCell>{r.admnNo}</TableCell>
                    {/* the database key for this row, so a figure on screen
                        can be chased straight into course_enrollment */}
                    <TableCell>{r.enrollmentId}</TableCell>
                    {/* blank when the enrolment points at a course that is no
                        longer in course_available, rather than showing an id */}
                    <TableCell>{r.course || ''}</TableCell>
                    <TableCell>
                      {/* the fee type is left exactly as the database has it. A
                          hold really is a Tuition Fee, it is the payment STATE
                          that differs, so it is marked beside the name rather
                          than replacing it. */}
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <span>{r.feeType}</span>
                        {r.isHold && (
                          <Tooltip title="A hold amount. It counts towards Revenue, but NOT towards Admissions or Sales, so this payment will not appear in either of those popups.">
                            <Chip
                              size="small"
                              variant="outlined"
                              color="warning"
                              label="Hold"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{inr(r.amount)}</TableCell>
                    <TableCell>{prettyDate(r.receivedOn)}</TableCell>
                    <TableCell>
                      {r.keyedByOga ? (
                        <Typography variant="body2" color="text.secondary">
                          The OGA
                        </Typography>
                      ) : (
                        // keyed by Finance for them, which still counts
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{r.keyedBy || ''}</Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            color="info"
                            label="Finance"
                          />
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <DeveloperNote
          title="Revenue is money RECEIVED in the period, credited to the OGA who owns the enrolment"
          steps={[
            {
              what: 'Start from the OGA',
              where: 'user.userID  =  ' + (person?.userId ?? ''),
              why: 'the person whose row was clicked on the table',
            },
            {
              what: 'Find the enrolments they own',
              where: 'course_enrollment  WHERE course_enrollment_userID = that id',
              why: 'ownership of the enrolment is what earns the money, whoever keyed the payment',
            },
            {
              what: 'Join the payments onto those enrolments',
              where: 'income.income_course_enrollment_id  =  course_enrollment.course_enrollment_id',
              why: 'one row per payment, so a student paying twice is two rows',
            },
            {
              what: 'Keep only payments dated inside the month',
              where: "income.income_transaction_date  BETWEEN '" + fromDate + "' AND '" + toDate + "'",
              why: 'the payment date, not the date the course was sold',
            },
            {
              what: 'Throw away the payments that do not count',
              where: 'income.delete_status <> 0 · income.income_accountant_verification = 2 · income.fee_type = EMI Processing Fee · course_enrollment.course_enrollment_status = -1',
              why: 'deleted, rejected by the accountant, processing fee, and voided enrolments',
            },
            {
              what: 'Check who keyed the payment in',
              where: 'income.userID  →  user_roles.user_id  →  usertype.usertypeID  →  designations.designationID  →  wing.dept_id',
              why: 'keep it only if income.userID is the same OGA, OR that person sits in the Finance department, wing.dept_id = 7. Anyone else and the payment is dropped',
            },
            {
              what: 'Add up what is left',
              where: 'SUM(income.income_amount)   =   Revenue',
              why: 'the amount exactly as stored, nothing scaled or converted',
            },
          ]}
          lines={[
            'HOLD AMOUNTS ARE COUNTED HERE. Revenue has no hold filter, unlike Admissions and Sales.',
            'Fee type is only used to drop EMI Processing Fee. Every other fee type counts towards Revenue.',
          ]}
        />
      </DialogContent>
    </Dialog>
  );
}
